import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Signal,
  signal,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {
  AvailableOperatorGroups,
  MappingEngineStore,
} from '@em/shared/mapping-engine/data-access';
import {
  CONDITION_FORM_BUILDER,
  MappingEngine,
  MappingEngineAttributes,
  MappingEngineOwner,
  MappingEngineShopSystem,
  MappingRuleList,
} from '@em/shared/mapping-engine/domain-models';
import {DialogService} from '@em/shared/ui';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {MappingEngineConditionFormBuilderService} from '../../services/mapping-engine-form-builder/mapping-engine-condition-form-builder.service';
import {MappingEngineFormBuilderService} from '../../services/mapping-engine-form-builder/mapping-engine-form-builder.service';
import {EmEditMappingRuleListModalComponent} from '../edit-mapping-rule-list-modal/edit-mapping-rule-list-modal.component';

@Component({
  selector: 'em-mapping-engine',
  templateUrl: './mapping-engine.component.html',
  styleUrls: ['./mapping-engine.component.scss'],
  providers: [
    MappingEngineFormBuilderService,
    MappingEngineStore,
    {
      provide: CONDITION_FORM_BUILDER,
      useClass: MappingEngineConditionFormBuilderService,
    },
  ],
  standalone: false,
})
export class EmMappingEngineComponent implements OnInit {
  private readonly _fb = inject(MappingEngineFormBuilderService);
  private readonly _conditionFormBuilder = inject(
    CONDITION_FORM_BUILDER,
  ) as MappingEngineConditionFormBuilderService;
  private readonly _mappingEngineStore = inject(MappingEngineStore);
  private readonly _dialogService = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  @Input() set uuid(value: string | null | undefined) {
    if (value) {
      this._mappingEngineStore.updateUuid(value);
    }
  }
  @Input() set owner(value: MappingEngineOwner | null | undefined) {
    if (value) {
      this._mappingEngineStore.updateOwner(value);
    }
  }
  // emptyString = noPlugin, null/undefined not loaded yet
  @Input() set pluginName(
    value: MappingEngineShopSystem | '' | null | undefined,
  ) {
    if (value !== null && value !== undefined) {
      this._mappingEngineStore.updateShopSystemName(value);
    }
  }
  @Input() set attributes(value: MappingEngineAttributes | null | undefined) {
    if (value) {
      this._mappingEngineStore.configureAttributes(value);
    }
  }
  @Input() supportDefault = true;
  @Input() supportReorder = false;
  @Input() showCancelBtn = false;
  // For using custom destination input (text instead of dropdown), but
  // send the "custom" flag always as false, used in csv export
  @Input() forceCustomWithoutFlag = false;
  @Output() requestAttributes = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();
  @Output() canceled = new EventEmitter<boolean>();

  readonly availableOperatorGroups = AvailableOperatorGroups;
  readonly mappingEngine$ = this._mappingEngineStore.mappingEngine$;
  readonly isDefault$ = this._mappingEngineStore.isDefault$;
  readonly isLoading$ = this._mappingEngineStore.isLoading$;
  readonly destinationAttributes = toSignal(
    this._mappingEngineStore.destinationAttributes$,
  );
  readonly sourceAttributes = toSignal(
    this._mappingEngineStore.sourceAttributes$,
  );
  readonly customSourceAttributes = toSignal(
    this._mappingEngineStore.customSourceAttributes$,
  );
  readonly showDefaultME$: Observable<boolean>;

  mappingRulesFormGroup$: Observable<UntypedFormGroup | null | undefined>;
  allMappingRulesControls: WritableSignal<AbstractControl[] | undefined> =
    signal(undefined);
  filteredMappingRulesControls: Signal<AbstractControl[] | undefined>;
  searchTerm: WritableSignal<string> = signal('');
  showNoRulesError = false;

  private readonly _mappingFormGroup$ =
    new BehaviorSubject<UntypedFormGroup | null>(null);
  private _oldFormValue: undefined | undefined;
  private readonly _showDefaultME$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.mappingRulesFormGroup$ = this._mappingFormGroup$.asObservable();

    this.filteredMappingRulesControls = computed(() => {
      const searchTerm = this.searchTerm();

      return this.allMappingRulesControls()?.filter((c) =>
        (c.value['attribute'] as string)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    });

    this.showDefaultME$ = this._showDefaultME$.asObservable();

    // Only request attributes when the mapping engine is not default
    // or user manually ask for it (to display the default engine)
    combineLatest([this.isDefault$, this._showDefaultME$])
      .pipe(
        map(([isDefault, showDefault]) => isDefault === false || showDefault),
        filter((value) => !!value),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe((request) => {
        if (request) {
          this.requestAttributes.emit(true);
        }
      });
  }

  ngOnInit(): void {
    combineLatest([
      this._mappingEngineStore.mappingEngine$.pipe(filter((ME) => !!ME)),
      this._mappingEngineStore.destinationAttributes$,
    ])
      .pipe(
        take(1),
        tap(([ME, destinationAttributes]) => {
          this._conditionFormBuilder.updateOperatorGroups(
            this.availableOperatorGroups,
          );
          const form = this._fb.buildMappingEngineForm(
            ME as MappingEngine,
            destinationAttributes,
          );
          this._mappingFormGroup$.next(form);
          this.allMappingRulesControls.set(
            (form.get('mappingRuleList') as UntypedFormArray)?.controls,
          );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();

    this.mappingRulesFormGroup$
      .pipe(
        filter((formGroup) => !!formGroup),
        take(1),
        tap((formGroup) => (this._oldFormValue = formGroup?.value)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();

    this._mappingEngineStore.saved$
      .pipe(
        filter((saved) => !!saved),
        tap(() => {
          const formGroup = this._mappingFormGroup$.value;
          if (formGroup) {
            // Update old form value to prevent unsaved changes warning after saving
            this._oldFormValue = formGroup.value;
          }
        }),
        tap((saved) => {
          this.saved.emit(saved);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  saveChanges() {
    const formGroup = this._mappingFormGroup$.value;

    if (formGroup) {
      if (formGroup?.valid) {
        if (!formGroup.value.mappingRuleList.length) {
          this.showNoRulesError = true;
        } else if (
          JSON.stringify(formGroup.value) === JSON.stringify(this._oldFormValue)
        ) {
          // user didn't make any changes
          this.saved.next(true);
        } else {
          this.isDefault$
            .pipe(
              take(1),
              switchMap((isDefault) =>
                this._dialogService.confirm({
                  title: isDefault
                    ? 'MAPPING_ENGINE_CONFIRM_SAVE_DEFAULT_TITLE'
                    : 'SHARED_CONFIRM_SAVE_TITLE',
                  description: isDefault
                    ? 'MAPPING_ENGINE_CONFIRM_SAVE_DEFAULT_TEXT'
                    : 'SHARED_CONFIRM_SAVE_TEXT',
                  confirmLabel: isDefault
                    ? 'MAPPING_ENGINE_CONFIRM_SAVE_DEFAULT_YES'
                    : 'SHARED_CONFIRM_SAVE_YES',
                  confirmType: 'primary',
                }),
              ),
            )
            .subscribe((confirm) => {
              if (confirm) {
                this._mappingEngineStore.saveMappingEngine(formGroup.value);
              }
            });
        }
      } else {
        formGroup?.markAllAsTouched();
      }
    } else {
      // form is not visible, user uses the default engine
      this.saved.emit(true);
    }
  }

  addNewRuleList(custom: boolean) {
    this.showNoRulesError = false;
    const newGroup = this._fb.addNewMappingRuleList(custom);
    this.rebindControls();
    this.openEditRuleList(newGroup);
  }

  showDefaultMappingEngine() {
    this._showDefaultME$.next(true);
    this._mappingEngineStore.loadMappingEngineDetails();
  }

  revertToDefault() {
    this._showDefaultME$.next(false);
    return this._dialogService
      .confirm({
        title: 'MAPPING_ENGINE_REVERT_TO_DEFAULT_TITLE',
        description: 'MAPPING_ENGINE_REVERT_TO_DEFAULT_TEXT',
        confirmLabel: 'MAPPING_ENGINE_REVERT_TO_DEFAULT_YES',
        confirmType: 'primary',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this._mappingEngineStore.revertToDefault();
        }
      });
  }

  onItemDrop(event: CdkDragDrop<string[]>) {
    this.changeOrder(event.previousIndex, event.currentIndex);
  }

  changeOrder(fromIndex: number, toIndex: number) {
    if (toIndex < 0) {
      return;
    }
    this.mappingRulesFormGroup$
      .pipe(
        take(1),
        tap((formGroup) => {
          if (formGroup) {
            const formArray = formGroup.get('mappingRuleList') as FormArray;

            if (toIndex < formArray.length) {
              const controlToMove = formArray.at(fromIndex);
              formArray.removeAt(fromIndex);
              formArray.insert(toIndex, controlToMove);
              formArray.updateValueAndValidity();
              this.rebindControls();
            }
          }
        }),
      )
      .subscribe();
  }

  allowDelete(ruleList: MappingRuleList): boolean {
    return !this.isRequired(ruleList);
  }

  isRequired(ruleList: MappingRuleList): boolean {
    return !!this.destinationAttributes()?.find(
      (da) => da.attribute === ruleList.attribute,
    )?.required;
  }

  openEditRuleList(ruleList: AbstractControl) {
    // get the rule index from the original list , not the filtered
    const index = this.getRuleOriginalIndex(ruleList);

    if (index !== undefined && index > -1) {
      const dialog_ref = this._matDialog.open(
        EmEditMappingRuleListModalComponent,
        {
          disableClose: true,
          data: {
            sourceAttributes: this.sourceAttributes(),
            customSourceAttributes: this.customSourceAttributes(),
            destinationAttributes: this.destinationAttributes(),
            arrayIndex: index,
            forceCustomWithoutFlag: this.forceCustomWithoutFlag,
            type: ruleList.value.attribute ? 'edit' : 'add',
          },
          viewContainerRef: this._viewContainerRef,
        },
      );

      dialog_ref.afterClosed().subscribe((deleteAdded) => {
        if (deleteAdded) {
          this._fb.removeMappingRuleList(index);
        }
        this.rebindControls();
      });
    }
  }

  rebindControls() {
    this.allMappingRulesControls.set([
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...(
        this._mappingFormGroup$.value?.get(
          'mappingRuleList',
        ) as UntypedFormArray
      )?.controls,
    ]);
  }

  getRuleOriginalIndex(ruleList: AbstractControl) {
    return this.allMappingRulesControls()?.indexOf(ruleList);
  }

  removeRuleList(ruleList: AbstractControl) {
    const index = this.getRuleOriginalIndex(ruleList);
    if (index || index === 0) {
      this._dialogService
        .confirm({
          title: 'PIPELINE_DELETE_RULE_LIST_TITLE',
          description: 'PIPELINE_DELETE_RULE_LIST_TEXT',
          confirmLabel: 'PIPELINE_DELETE_RULE_LIST_YES',
          confirmType: 'danger',
        })
        .subscribe((confirmed) => {
          if (confirmed && (index || index === 0)) {
            this._fb.removeMappingRuleList(index);
            this.rebindControls();
          }
        });
    }
  }

  hasUnsavedChanges() {
    const formGroup = this._mappingFormGroup$.value;
    return (
      formGroup?.value &&
      JSON.stringify(formGroup.value) !== JSON.stringify(this._oldFormValue)
    );
  }
}
